import { PenIcon } from "@xola/icons";
import { FormGroup, Label, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import { omit } from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
    AndFilter,
    BaseSchedule,
    GiftOffer,
    GreaterThanOrEqualToFilter,
    InFilter,
    InScheduleFilter,
    Permission,
    ProductObject,
    PurchaseRule,
    PurchaseRuleFilterObject,
    SelectionState,
    ValidationLevel,
} from "types";
import { ProductSelectorModal } from "../Common/ProductSelectorModal";
import { PromptModal } from "../Common/PromptModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { SelectedProductList } from "../Common/SelectedProductList";
import { XolaFormik } from "../Common/XolaFormik";
import { getBlackoutSchedules, getWindowSchedule } from "../Coupon/CouponRestrictionForm";
import { ContentLayout } from "../Layout/ContentLayout";
import { getGiftOfferTerm } from "./EditGiftOffer";
import { GiftOfferScheduleEditor } from "./GiftOfferScheduleEditor";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";
import { createAndFilter, createInFilter, createInScheduleFilter } from "@/models/RuleFilter";

export interface EditGiftOfferRestrictionsProps {
    giftOffer: GiftOffer;
    onSubmit: (params: GiftOffer) => Promise<GiftOffer | void>;
    onCancel: () => void;
    purchaseRule: PurchaseRule;
    redemptionPurchaseRule: PurchaseRule;
    isNewGift: boolean;
    deletedExperiencesIdList: ID[];
}

export const EditGiftOfferRestrictions = ({
    giftOffer,
    onSubmit,
    onCancel,
    purchaseRule,
    redemptionPurchaseRule,
    isNewGift,
    deletedExperiencesIdList,
}: EditGiftOfferRestrictionsProps) => {
    const history = useHistory();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const isMerchandiseAvailable = isGranted(Permission.MERCHANDISE_MODIFY);
    const [productSelectorModalOpen, setProductSelectorModalOpen] = useState(false);
    const [formTouched, setFormTouched] = useState(false);
    const [isConfirmationModalOpen, { show: showConfirmationModal }] = useVisibility(false);

    const [purchaseRuleState, setPurchaseRuleState] = useState(purchaseRule);
    const [redemptionPurchaseRuleState, setRedemptionPurchaseRuleState] = useState(redemptionPurchaseRule);

    const giftOfferVariableTerm = getGiftOfferTerm(purchaseRule);
    const purchaseWindowSchedule = getWindowSchedule(purchaseRuleState, "currentTime");
    const purchaseBlackoutSchedules = getBlackoutSchedules(purchaseRuleState, "currentTime");

    const arrivalWindowSchedule = getWindowSchedule(redemptionPurchaseRuleState, "arrivalDateTime");
    const arrivalBlackoutSchedules = getBlackoutSchedules(redemptionPurchaseRuleState, "arrivalDateTime");
    const productRestrictions = getProductRestrictions(redemptionPurchaseRuleState, "product.id");

    const [productRestrictionsSelection, setProductRestrictionsSelection] = useState<SelectionState>(
        productRestrictions
            ? {
                  all: productRestrictions.length === 0,
                  selected: productRestrictions.filter((id) => !deletedExperiencesIdList.includes(id)),
              }
            : { all: true, selected: [] },
    );

    const [error, setError] = useState<string | null>(null);

    const hasSelectedExperiences = productRestrictionsSelection.all || productRestrictionsSelection.selected.length > 0;

    const handleConfirmation = async (values: Partial<GiftOffer>, applyToPastPurchases = false) => {
        const cascadeToClones = applyToPastPurchases ? { _cascadeToClones: ["filter"] } : null;
        if (hasSelectedExperiences) {
            setError(null);
            await onSubmit({
                ...giftOffer,
                ...omit(values, "isFormDirty"),
                purchaseRule: { ...purchaseRuleState },
                redemptionPurchaseRule: { ...redemptionPurchaseRuleState, ...cascadeToClones },
            });
            setFormTouched(true);
            history.push(`/products/giftOffers`);
        } else {
            setError("- Select one or more experiences");
        }

        invalidate("purchaseRules");
    };

    const handleSubmit = async (values: Partial<GiftOffer>) => {
        if (formTouched) {
            // When a new gift is created, and when we change the restrictions, we should not show the confirmation modal
            // To know if this is a newly created gift, we check if the purchaseRule.createdAt is from now
            if (isNewGift) {
                await handleConfirmation(values, false);
            } else {
                showConfirmationModal();
            }
        } else {
            history.push(`/products/giftOffers/${giftOffer?.id}/photos`);
        }
    };

    const updatePurchaseRule = (values: Partial<PurchaseRule>) => {
        setPurchaseRuleState({ ...purchaseRuleState, ...values });
    };

    const updateRedemptionPurchaseRule = (values: Partial<PurchaseRule>) => {
        setRedemptionPurchaseRuleState({ ...redemptionPurchaseRuleState, ...values });
    };

    const handlePurchaseWindowScheduleChange = (schedule: BaseSchedule | null) => {
        const filter = getPurchaseRuleFilter(giftOfferVariableTerm, schedule, purchaseBlackoutSchedules);

        updatePurchaseRule({ filter });
    };

    const handlePurchaseBlackoutSchedulesChange = (schedules: BaseSchedule[]) => {
        const filter = getPurchaseRuleFilter(giftOfferVariableTerm, purchaseWindowSchedule, schedules);

        updatePurchaseRule({ filter });
    };

    const handleArrivalWindowScheduleChange = (schedule: BaseSchedule | null) => {
        const filter = getRedemptionPurchaseRuleFilter(
            schedule,
            arrivalBlackoutSchedules,
            productRestrictionsSelection,
        );

        updateRedemptionPurchaseRule({ filter });
    };

    const handleArrivalBlackoutSchedulesChange = (schedules: BaseSchedule[]) => {
        const filter = getRedemptionPurchaseRuleFilter(arrivalWindowSchedule, schedules, productRestrictionsSelection);

        updateRedemptionPurchaseRule({ filter });
    };

    const handleProductRestrictionsChange = (selection: SelectionState) => {
        const filter = getRedemptionPurchaseRuleFilter(arrivalWindowSchedule, arrivalBlackoutSchedules, selection);

        setProductRestrictionsSelection(selection);
        updateRedemptionPurchaseRule({ filter });
    };

    const initialValues: Partial<GiftOffer> & { isFormDirty?: boolean } = {
        emailReminder: giftOffer.emailReminder,
        emailReminderDays: giftOffer.emailReminderDays ?? 2,
        isFormDirty: false,
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                initialValues={initialValues}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, isSubmitting, values, setFieldValue }) => {
                    return (
                        <div className="pb-10">
                            <h2>Restrictions</h2>
                            <div className="divide-y divide-gray-light">
                                <ContentLayout.Section
                                    title="Purchase Window"
                                    description="Restricts the time period in which the gift offer can be purchased"
                                >
                                    <GiftOfferScheduleEditor
                                        title="Purchase Window"
                                        description="Define the window of time within which a purchase must be made in order to qualify for the discount. Note, that this is independent of the date of the event when the service will be provided."
                                        windowSchedule={purchaseWindowSchedule}
                                        blackoutSchedules={purchaseBlackoutSchedules}
                                        onWindowScheduleChange={(schedule) => {
                                            handlePurchaseWindowScheduleChange(schedule);
                                            setFieldValue("isFormDirty", true);
                                            setFormTouched(true);
                                        }}
                                        onBlackoutSchedulesChange={(schedule) => {
                                            handlePurchaseBlackoutSchedulesChange(schedule);
                                            setFieldValue("isFormDirty", true);
                                            setFormTouched(true);
                                        }}
                                    />
                                </ContentLayout.Section>

                                <ContentLayout.Section
                                    title="Arrival Window"
                                    description="Limits the start date and start time of the events that this code can be applied to"
                                >
                                    <GiftOfferScheduleEditor
                                        title="Arrival Window"
                                        description="Define the window of time within which the event will take place and the services provided."
                                        windowSchedule={arrivalWindowSchedule}
                                        blackoutSchedules={arrivalBlackoutSchedules}
                                        onWindowScheduleChange={(schedule) => {
                                            handleArrivalWindowScheduleChange(schedule);
                                            setFieldValue("isFormDirty", true);
                                            setFormTouched(true);
                                        }}
                                        onBlackoutSchedulesChange={(schedule) => {
                                            handleArrivalBlackoutSchedulesChange(schedule);
                                            setFieldValue("isFormDirty", true);
                                            setFormTouched(true);
                                        }}
                                    />
                                </ContentLayout.Section>

                                <ContentLayout.Section
                                    title="Products"
                                    description="Select if you would like this coupon to be applicable to all products, or select individual ones"
                                >
                                    <FormGroup className="space-y-2">
                                        <div className="flex items-end">
                                            <Label
                                                className={clsx(
                                                    "flex-grow",
                                                    error && !hasSelectedExperiences && "!text-danger",
                                                )}
                                            >
                                                Products {!hasSelectedExperiences && error}
                                            </Label>
                                            <SecondaryButton
                                                size="small"
                                                onClick={() => setProductSelectorModalOpen(true)}
                                            >
                                                <PenIcon />
                                            </SecondaryButton>
                                        </div>
                                        <ProductSelectorModal
                                            isOpen={productSelectorModalOpen}
                                            value={productRestrictionsSelection}
                                            objects={[
                                                ProductObject.EXPERIENCE,
                                                ProductObject.MEMBERSHIP,
                                                ProductObject.PACKAGE,
                                                ...(isMerchandiseAvailable ? [ProductObject.WARE] : []),
                                            ]}
                                            groupBy="object"
                                            exclude={[giftOffer.id]}
                                            onClose={() => setProductSelectorModalOpen(false)}
                                            onConfirm={(selection) => {
                                                handleProductRestrictionsChange(selection);
                                                setFieldValue("isFormDirty", true);
                                                setFormTouched(true);
                                            }}
                                        />
                                        <SelectedProductList
                                            selectedProducts={
                                                productRestrictionsSelection ?? { all: true, experiences: [] }
                                            }
                                        />
                                    </FormGroup>
                                </ContentLayout.Section>

                                <PromptModal
                                    title="Apply new terms to past gift purchases?"
                                    color="primary"
                                    dialogText={{ submit: "Yes", secondary: "No" }}
                                    isLoading={isSubmitting}
                                    isOpen={isConfirmationModalOpen}
                                    onConfirm={async () => {
                                        handleConfirmation(values, true);
                                        setFieldValue("isFormDirty", false);
                                        setFormTouched(true);
                                    }}
                                    onClose={() => {
                                        handleConfirmation(values, false);
                                        setFieldValue("isFormDirty", false);
                                        setFormTouched(true);
                                    }}
                                >
                                    There could be existing purchases for the current terms of this gift offer. Do you
                                    want to apply the new terms to existing gift offer purchases?
                                </PromptModal>

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                                        Save
                                    </SubmitButton>

                                    <SecondaryButton type="reset" onClick={onCancel}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>

                                <ScrollToFieldError />
                            </div>
                        </div>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

export const getProductRestrictions = (purchaseRule: PurchaseRule, field: string): string[] => {
    if (purchaseRule.filter?.object !== PurchaseRuleFilterObject.AND_FILTER) {
        return [];
    }

    const filters = purchaseRule.filter?.filters;
    if (!filters) {
        return [];
    }

    const productRestrictions = filters.filter((filter) => {
        return filter.object === PurchaseRuleFilterObject.IN_FILTER;
    }) as InFilter[];

    const filter = productRestrictions.find((filter) => {
        return filter.operand?.some((id) => {
            return id && filter.field === field;
        });
    });

    return (filter?.operand as [string]) ?? [];
};

// Create a purchase rule object from the provided schedules.
const getPurchaseRuleFilter = (
    giftOfferVariableTerm: GreaterThanOrEqualToFilter | null = null,
    purchaseWindowSchedule: BaseSchedule | null = null,
    purchaseBlackoutSchedules: BaseSchedule[] = [],
): AndFilter => {
    const filters: Array<InScheduleFilter | InFilter | GreaterThanOrEqualToFilter> = [];

    if (giftOfferVariableTerm) {
        filters.push(giftOfferVariableTerm);
    }

    if (purchaseWindowSchedule) {
        filters.push(
            createInScheduleFilter({
                schedules: [purchaseWindowSchedule],
                field: "currentTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    if (purchaseBlackoutSchedules.length > 0) {
        filters.push(
            createInScheduleFilter({
                schedules: purchaseBlackoutSchedules,
                field: "currentTime",
                validationLevel: ValidationLevel.ERROR,
            }),
        );
    }

    return createAndFilter({ filters });
};

const getRedemptionPurchaseRuleFilter = (
    arrivalWindowSchedule: BaseSchedule | null = null,
    arrivalBlackoutSchedules: BaseSchedule[] = [],
    productRestrictions: SelectionState | null = null,
): AndFilter => {
    const filters: Array<InScheduleFilter | InFilter> = [];

    if (arrivalWindowSchedule) {
        filters.push(
            createInScheduleFilter({
                schedules: [arrivalWindowSchedule],
                field: "arrivalDateTime",
                validationLevel: ValidationLevel.INFO,
            }),
        );
    }

    if (arrivalBlackoutSchedules.length > 0) {
        filters.push(
            createInScheduleFilter({
                schedules: arrivalBlackoutSchedules,
                field: "arrivalDateTime",
                validationLevel: ValidationLevel.INFO,
            }),
        );
    }

    if (productRestrictions?.all === false) {
        filters.push(
            createInFilter({
                operand: productRestrictions?.selected,
                field: "product.id",
                validationLevel: ValidationLevel.INFO,
            }),
        );
    }

    return createAndFilter({ filters });
};
